<template>
  <div
    id="ips"
    class="mb-6"
  >
    <!-- In VSCode you can use ALT + z to turn on word wrap (or View > Word Wrap)  -->

    <section v-if="mission">
      <h3>Our Mission</h3>
      <p class="inserted-text">
        {{ mission }}
      </p>
    </section>


    <section>
      <h3>Purpose Statement</h3>
      <p>
        This statement summarizes <span class="inserted-text">{{ name }}</span>'s approach and strategic objectives with respect to Responsible Investing.
      </p>
    </section>


    <section>
      <h3>Responsible Investing Defined</h3>
      <p>
        Responsible Investing is the way an investor expresses social, environmental, or faith-based values, and/or views on the relationship environmental, social, and governance (ESG) factors with financial performance. It is driven by a growing recognition in the financial community that effective research, analysis and evaluation of ESG factors can be fundamental in assessing the performance of an investment, and a growing desire among investors to align their portfolios with their values and/or seek to create positive social and environmental impact. Responsible Investing can inform an investor's approach to asset allocation, stock selection, portfolio construction, shareholder engagement, and proxy voting.
      </p>
    </section>


    <section>
      <h3>Responsible Investing Policy Development Process</h3>
      <p>
        The Investment Committee of <span class="inserted-text">{{ name }}</span> developed this Responsible Investing Policy through a process of translating <span class="inserted-text">{{ name }}</span>'s goals, mission, and values into a plan for the organization's investments.
      </p>
    </section>


    <section>
      <h3>Responsible Investing Roles and Responsibilities</h3>
      <p>
        The chair of the Investment Committee is responsible for gathering feedback and maintaining the Responsible Investing policy in response to changes in the world and the organization. It is the responsibility of the investment committee to monitor the implementation of the Responsible Investing policy and adherence to its objectives and requirements.
      </p>
    </section>


    <section v-if="goals && goals.length > 0">
      <h3>Responsible Investing Motivations</h3>

      <div
        v-for="goal of goals"
        :key="goal.name"
      >
        <h4 class="inserted-text">
          {{ goal.name }}
        </h4>
        <p
          v-if="goal.ips"
          class="inserted-text"
        >
          {{ goal.ips }}
        </p>
      </div>
    </section>


    <section v-if="metrics && metrics.length > 0">
      <h3>Responsible Investing Values</h3>
      <p v-if="values && values.length > 0">
        <span class="inserted-text">{{ name }}</span> will focus its Responsible Investing on
        <span
          v-for="(value, i) of values"
          :key="value.name"
          class="inserted-text"
        >
          <span v-if="i !== 0 && values.length > 2">, </span>
          <span v-if="i === values.length - 1">and </span>
          {{ value.name }}</span>.

        <span class="inserted-text">{{ name }}</span> will specifically focus on the following issues:
      </p>

      <p v-else>
        <span class="inserted-text">{{ name }}</span> will focus its Responsible Investing on the following issues:
      </p>

      <!-- Class 'content' uses Bulma to render lists correctly -->
      <div class="content">
        <ul>
          <template v-for="group of displayMetrics">
            <li
              v-if="group.metrics.length > 0"
              :key="group.type"
            >
              <span class="inserted-text">{{ group.type }}:</span>
              <ul>
                <li
                  v-for="metric of group.metrics"
                  :key="metric.id"
                >
                  <span class="inserted-text">{{ metric.name }}</span>
                </li>
              </ul>
            </li>
          </template>
        </ul>
      </div>

      <p>
        Consistent with the goals of board members, the Foundation will generally prefer investments in companies with strong environmental, social, and governance records.
      </p>
    </section>


    <section v-if="strategies?.length">
      <h3>Responsible Investing Strategies</h3>
      <p>
        To pursue <span class="inserted-text">{{ name }}</span>'s goals
        <span v-if="goals?.length">
          (<span
            v-for="(goal, i) of goals"
            :key="goal.name"
            class="inserted-text"
          ><span v-if="i !== 0">, </span>{{ goal.name }}</span>)
        </span>

        and in support of <span class="inserted-text">{{ name }}</span>'s values
        <span v-if="values && values.length > 0">
          (<span
            v-for="(value, i) of values"
            :key="value.name"
            class="inserted-text"
          ><span v-if="i !== 0">, </span>{{ value.name }}</span>)
        </span>

        the following strategies will be employed.
      </p>

      <p>
        <span class="inserted-text">{{ name }}</span> will employ the following strategies:
        <span
          v-for="(strategy, i) of strategies"
          :key="strategy.name"
          class="inserted-text"
        >
          <span v-if="i !== 0 && strategies.length > 2">, </span>
          <span v-if="i === strategies.length - 1 && strategies.length > 1">and </span>
          {{ strategy.name }}</span>.

        <template v-if="allocations?.length">
          These strategies will be reflected in the following asset classes:
          <span
            v-for="(asset, i) of allocations"
            :key="asset.type"
          >
            <span class="inserted-text"><span v-if="i !== 0">, </span>{{ asset.name }}</span>
          </span>
        </template>
      </p>

      <p v-if="includeHighImpact">
        <span class="inserted-text">{{ name }}</span> will also set aside a specific pool for high-impact investments. <span class="inserted-text">{{ name }}</span>’s high-impact pool is not subject to the same risk/return expectations as the rest of the portfolio. These investments may be used to address market failures, bridge market gaps, and test new business models in pursuit of <span class="inserted-text">{{ name }}</span>'s mission. The high-impact pool does not roll up into the asset allocation, and will have separate performance reporting.
      </p>
    </section>

    <section>
      <h3>Reporting requirements</h3>
      <p>
        The Investment Committee will review quarterly reports on how the portfolio fits with the stated Responsible Investing goals, including updates on investment selection and shareholder engagement activities.
      </p>
    </section>

    <section>
      <h3>Process for updating IPS</h3>
      <p>
        <span class="inserted-text">{{ name }}</span> recognizes that from time to time issues may emerge around particular industries which raise questions about the Responsible Investing considerations we are committed to in our investments. We review <span class="inserted-text">{{ name }}</span>'s Responsible Investing statement annually, and will call special meetings if particular issues requiring urgent attention are brought to the attention of the Investment Committee.
      </p>
    </section>
  </div>
</template>


<script>
// import SubComponent from '../components/SubComponent'

export default {
  name: 'ComponentName',
  components: { /* Subcomponents */ },
  props: ['group', 'mission', 'goals', 'metrics', 'strategies', 'allocations', 'includeHighImpact'],
  data() {
    return {
      values: [],
    };
  },
  computed: { /*
    Creates a new property
    Updates when any dependant property changes */
    name() {
      if (this.group && this.group.name) return this.group.name;
      return 'Organization';
    },
    metricsExclusionary() {
      return this.metrics.filter(m => {
        return m.less_is_more && m.is_binary;
      });
    },
    metricsTiltAway() {
      return this.metrics.filter(m => {
        return m.less_is_more && !m.is_binary;
      });
    },
    metricsTiltTowards() {
      return this.metrics.filter(m => {
        return !m.less_is_more;
      });
    },
    displayMetrics() {
      return [
        { type: 'Exclusionary', metrics: this.metricsExclusionary },
        { type: 'Tilt Away', metrics: this.metricsTiltAway },
        { type: 'Tilt Towards', metrics: this.metricsTiltTowards },
      ];
    },
  },
  watch: { /*
    Watches an existing property
    Only runs when the watched property changes */
  },
  beforeCreate() {},
  created() {},
  updated() {},
  methods: {},
};
</script>


<style scoped>
#ips {
  width: 50em;
}
section {
  padding-bottom: 1em;
}
h3 {
  font-weight: bold;
}
h4 {
  text-decoration: underline;
}
p {
  padding-bottom: 1em;
}

/* .inserted-text {
  background-color: lightsalmon;
} */
</style>
